import { type CSSProperties, type ReactNode } from 'react';
import { surfaces } from '@qd/design-tokens';
import { CONTINENTS } from '@qd/map';

interface SlateBackgroundProps {
  children?: ReactNode;
  /** Maintained for backwards compatibility with props, no longer renders dust. */
  particles?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function SlateBackground({
  children,
  className,
  style,
  showWorldMap,
}: SlateBackgroundProps & { showWorldMap?: boolean }) {
  return (
    <div
      className={className}
      style={{
        position: 'relative',
        minHeight: '100dvh',
        width: '100%',
        // A vibrant ocean depth gradient
        background: `radial-gradient(circle at top, ${surfaces.slate} 0%, ${surfaces.slateShadow} 100%)`,
        color: surfaces.chalk,
        overflow: 'hidden',
        ...style,
      }}
    >
      {/* Full-screen faint waves */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `url('data:image/svg+xml;utf8,<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path d="M0,50 Q25,30 50,50 T100,50" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="2"/></svg>')`,
          backgroundSize: '150px 150px',
          animation: 'wave-pan 30s linear infinite',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {showWorldMap && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.35 }}>
          <svg viewBox="0 0 1000 650" style={{ width: '120%', maxWidth: 1600, filter: 'drop-shadow(0px 4px 10px rgba(0,0,0,0.5))' }}>
            {CONTINENTS.map((c) => (
              <path key={c.id} d={c.pathD} fill="rgba(214, 196, 154, 0.5)" stroke={surfaces.chalk} strokeWidth="2.5" strokeLinejoin="round" />
            ))}
          </svg>
        </div>
      )}
      {/* Animated Ocean Waves */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: 300,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        <div className="qd-ocean-wave qd-wave-back" />
        <div className="qd-ocean-wave qd-wave-front" />
      </div>

      {/* Children stack above the ocean layers. */}
      <div style={{ position: 'relative', zIndex: 2, minHeight: '100dvh' }}>
        {children}
      </div>

      {/* Inline styles for the wave animation */}
      <style>{`
        .qd-ocean-wave {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 200%;
          height: 100%;
          background: url('data:image/svg+xml;utf8,<svg viewBox="0 0 1200 150" xmlns="http://www.w3.org/2000/svg"><path d="M0,75 C150,150 350,0 500,75 C650,150 850,0 1000,75 C1150,150 1350,0 1500,75 C1650,150 1850,0 2000,75 L2400,75 L2400,150 L0,150 Z" fill="rgba(255,255,255,0.25)"/></svg>') repeat-x;
          background-size: 1200px 100%;
        }
        .qd-wave-back {
          animation: wave-anim-reverse 14s linear infinite;
          opacity: 0.7;
          bottom: 20px;
        }
        .qd-wave-front {
          animation: wave-anim 10s linear infinite;
          opacity: 1;
        }
        
        @keyframes wave-anim {
          0% { transform: translateX(0); }
          100% { transform: translateX(-1200px); }
        }
        @keyframes wave-anim-reverse {
          0% { transform: translateX(-1200px); }
          100% { transform: translateX(0); }
        }
        @keyframes wave-pan {
          0% { background-position: 0 0; }
          100% { background-position: -150px 150px; }
        }
        
        @media (prefers-reduced-motion: reduce) {
          .qd-ocean-wave {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
